import { Box, Button } from '@mui/material'
import { usePlanningStore } from '@planning/AppProvider'
import { GateInViewStore } from '@planning/stores/gateControl/GateInViewStore'
import {
  fetchHoldTypes,
  getHoldEnforcementResult,
  masterHoldTypes,
} from '@planning/utils/hold-enforcement'
import { canOrderProceed } from '@planning/utils/order-utils'
import { useTranslate } from '@tolgee/react'
import { Breadcrumbs, ButtonOption, ButtonWithDropdown, Header } from '@tom-ui/ui'
import { useHistory } from '@tom-ui/utils'
import _ from 'lodash'
import { observer } from 'mobx-react-lite'
import { BaseSyntheticEvent, FC, useCallback, useEffect } from 'react'

interface ITruckAppointmentPageHeaderProps {
  viewStore: GateInViewStore
  onSubmit: (e?: BaseSyntheticEvent<object, any, any> | undefined) => Promise<void>
}
interface ITruckVisitPageHeaderActionsProps extends ITruckAppointmentPageHeaderProps {
  onGoBack: () => void
}

const TruckVisitPageHeaderActions: FC<ITruckVisitPageHeaderActionsProps> = observer(
  ({ viewStore, onSubmit, onGoBack }) => {
    const { t } = useTranslate()
    const { appViewStore, tenantStore, holdItemStore, containerJourneyItemStore } =
      usePlanningStore()

    const { notificationStore, isTruckAppointment, externalDriver } = viewStore

    useEffect(() => {
      fetchHoldTypes() // ensures masterHoldTypes observable is populated
    }, [])

    useEffect(() => {
      const orders = [...viewStore.dropOffOrders, ...viewStore.pickUpOrders]
      const journeyIds = orders.flatMap(o => {
        const jId = (o.journeyId ||
          containerJourneyItemStore.journeysByInboundOrderId[o.id]?.id ||
          containerJourneyItemStore.journeysByOutboundOrderId[o.id]?.id ||
          (o.containerId && containerJourneyItemStore.journeysByContainerId[o.containerId]?.id)) as
          | number
          | undefined
        return jId ? [jId] : []
      })
      if (journeyIds.length) holdItemStore.fetchHoldsByContainerJourneyIds(journeyIds)
    }, [viewStore.dropOffOrders, viewStore.pickUpOrders, holdItemStore, containerJourneyItemStore])

    const onGateIn = useCallback(() => {
      viewStore.setIsTruckAppointment(false)
      onSubmit()
    }, [viewStore, onSubmit])

    const masterTypes = masterHoldTypes.get()
    const orders = [...viewStore.dropOffOrders, ...viewStore.pickUpOrders]

    const anyHoldBlocked = orders.some(o => {
      // Resolve journey ID — prefer journeyId on order, fall back to store lookups
      const jId = (o.journeyId ||
        containerJourneyItemStore.journeysByInboundOrderId[o.id]?.id ||
        containerJourneyItemStore.journeysByOutboundOrderId[o.id]?.id ||
        (o.containerId && containerJourneyItemStore.journeysByContainerId[o.containerId]?.id) ||
        (o.containerNumber &&
          _(containerJourneyItemStore.elements)
            .values()
            .find((j: any) => j.container?.data.number === o.containerNumber)?.id)) as
        | number
        | undefined

      const holds = holdItemStore.holdsByContainerJourneyId[jId ?? -1] ?? []

      return getHoldEnforcementResult(holds, 'GateIn', o.direction, masterTypes).isBlocking
    })

    const validateNoOrderVisit = !anyHoldBlocked && (
      orders.some(o =>
        canOrderProceed({
          allowGateInWhenUnitNotOnTerminal: tenantStore.allowGateInWhenUnitNotOnTerminal,
          direction: o.direction,
          hasBlockingHold: false,
          isOnTerminal: o.isOnTerminal,
          skipReleaseOrder: tenantStore.skipReleaseOrder,
        }),
      ) ||
      viewStore.nnrOrders.some(o => o.suitableContainersCount) ||
      viewStore.dropOffGeneralCargoOrders?.length > 0 ||
      viewStore.pickUpGeneralCargoOrders?.length > 0
    )

    const getCreateVisitOptions = () => {
      const options: ButtonOption[] = [
        {
          label: t('gateIn', 'Gate in'),
          testId: 'gate-in-submit-btn',
          onClick: onGateIn,
          disabled: externalDriver?.banned || !validateNoOrderVisit,
        },
        {
          label: t('cancelVisit', 'Cancel visit'),
          testId: 'deleteTruckAppointment',
          onClick: async () => {
            const isConfirmed = await appViewStore.setOpenConfirmDialog(
              true,
              t(
                'truckVisitWillBeCancelledDoYouWantToProceed',
                'Truck visit will be cancelled. Do you want to proceed?',
              ),
              t('cancelVisit', 'Cancel visit'),
            )

            if (isConfirmed) {
              const truckVisitId = viewStore.truckVisitId!
              try {
                await notificationStore.deleteTruckAppointment(truckVisitId)
                appViewStore.setShowAlert(
                  'success',
                  t('truckVisitCancelled', 'Truck visit cancelled'),
                )
                onGoBack()
              } catch (error) {
                appViewStore.setShowAlert(
                  'error',
                  t('errorCancellingTruckAppointment', 'Error cancelling truck visit'),
                )
              }
            }
          },
        },
      ]

      return options
    }

    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            placeContent: 'flex-end',
            placeItems: 'center',
            alignItems: 'center',
            width: '100%',
          }}
          gap={2}
        >
          <Button key='cancelGateIn' onClick={onGoBack} variant='text' color='inherit'>
            {t('cancel', 'Cancel')}
          </Button>

          {isTruckAppointment && viewStore.truckVisitId && (
            <ButtonWithDropdown
              label={t('manage', 'Manage')}
              color='inherit'
              options={getCreateVisitOptions()}
            />
          )}

          {isTruckAppointment && (
            <Button
              key='confirm'
              onClick={onSubmit}
              variant={'contained'}
              type='submit'
              data-cy='truck-appointment-save-btn'
            >
              {t('save', 'Save')}
            </Button>
          )}

          {!isTruckAppointment && (
            <Button
              data-cy='gate-in-submit-btn'
              key='confirmGateIn'
              variant='contained'
              onClick={onGateIn}
            >
              {t('gateIn', 'Gate In')}
            </Button>
          )}
        </Box>
      </>
    )
  },
)

export const TruckAppointmentPageHeader: FC<ITruckAppointmentPageHeaderProps> = observer(
  ({ viewStore, onSubmit }) => {
    const { t } = useTranslate()
    const history = useHistory()

    const onGoBack = () => {
      viewStore.reset()
      history.goBackHistoryOrDefault('/truck-visits')
    }

    const title = viewStore.isTruckAppointment
      ? viewStore.truckVisitId
        ? t('editTruckVisit', 'Edit truck visit')
        : t('createTruckVisit', 'Create truck visit')
      : t('gateIn', 'Gate in')

    const breadCrumbs = [
      {
        label: t('truckVisits', 'Truck visits'),
        onClick: onGoBack,
      },
      {
        label: title,
      },
    ]

    return (
      <>
        <Breadcrumbs items={breadCrumbs} />
        <Header
          title={title}
          rightRenderOption={
            <TruckVisitPageHeaderActions
              viewStore={viewStore}
              onSubmit={onSubmit}
              onGoBack={onGoBack}
            />
          }
        />
      </>
    )
  },
)
